import {isEqual, isEqualWith} from 'lodash-es';
import {isMoment} from 'moment-timezone';
import {compareMoments, momentsEqual} from '@shared/util/comparitors/moment-comparator';

export function objectsEqual(a: any, b: any): boolean {
  // TODO Check if nulls and undefined are equal to each other
  return isEqual(a, b);
}

export function objectsEqualWith<T = any, T1 = any>(a: T, b: T1, customizer: (a: T, b: T1) => boolean | undefined): boolean {
  // TODO Check if nulls and undefined are equal to each other
  return isEqualWith(a, b, customizer);
}

function strictEqualExceptEmpty(a: unknown, b: unknown): boolean {
  return (a == null && b == null) || (a === b);
}

export function equalByPropByFn<T, K extends keyof T, P = T[K]>(obj1: T, obj2: T, getProp: (obj: T) => P,
  comparer: (prop1: P, prop2: P) => boolean = strictEqualExceptEmpty): boolean {
  return objectsEqualWith(getProp(obj1), getProp(obj2), comparer);
}

export function nullCompare<T = any>(a: T, b: T, compareNotNullValuesFn: (a1: T, b1: T) => number, nullsFirst = true): number {
  if (a == null) {
    return b == null ? 0 : (nullsFirst ? -1 : 1);
  } else {
    return b == null ? (nullsFirst ? 1 : -1) : compareNotNullValuesFn(a, b);
  }
}

/**
 * Expecting a and b to be concrete values, i.e. not null or undefined
 */
function defaultCompareNotNullValues(a: any, b: any): number {
  // By default when a sort() method of Array is used, JavaScript converts array members to strings to compare them
  // So use the same approach as the default sort
  const compareAsStrings = (v1: any, v2: any) => v1.toString().localeCompare(v2.toString());
  const t = typeof a;
  switch (t) {
    case 'boolean':
    case 'number':
      return a - b;
    case 'string':
      return a.localeCompare(b);
    case 'object':
      if (isMoment(a)) {
        return compareMoments(a, b);
      } else {
        return compareAsStrings(a, b);
      }
    default:
      return compareAsStrings(a, b);
  }
}

function accessProperty(a: any, props: string[]): any {
  let value = a;
  for (let i = 0; i < props.length; i++) {
    value = value[props[i]];
    if (value == null) {
      return value;
    }
  }
  return value;
}

export function valueCompare(a: any, b: any, nullFirst = true): number {
  return nullCompare(a, b, defaultCompareNotNullValues, nullFirst);
}

export function objectsCompareByFn<T, T1>(a: T, b: T, mapFn: (obj: T) => T1, nullsFirst = true): number {
  return nullCompare(a, b, (a1, b1) => nullCompare(mapFn(a1), mapFn(b1), defaultCompareNotNullValues, nullsFirst), nullsFirst);
}

export function objectsCompareByNestedProperties<T>(a: T, b: T, props: string[], nullsFirst = true): number {
  return objectsCompareByFn(a, b, obj => accessProperty(obj, props), nullsFirst);
}

export function objectsCompareByProperty<T, K extends keyof T>(a: T, b: T, prop: K, nullsFirst = true): number {
  return objectsCompareByNestedProperties(a, b, <string[]>[prop], nullsFirst);
}

export function objectEqualMomentAware(a: any, b: any): boolean {
  return objectsEqualWith(a, b, (m1, m2) => isMoment(m1) && isMoment(m2) ? momentsEqual(m1, m2) : undefined);
}
